import { Context, Metric, Publisher } from '@amzn/katal-metrics';
import KatalMetricsDriverSushi from '@amzn/katal-metrics-driver-sushi';
import KatalMetricsDriverConsoleLogJson from '@amzn/katal-metrics/lib/driver/KatalMetricsDriverConsoleLogJson';
import detectDomainRealm from './detectMetricsDomainRealm';

const SERVICE_NAME = 'VendorCentralTrim';

function getSiteName() {
    const siteName = document?.querySelector(`[name="a2z:mons_site_name"]`)?.getAttribute('content') || 'unknownSite';
    return siteName.charAt(0).toUpperCase() + siteName.slice(1); // Katal prefers Pascal case
}

function createConsoleLogMetricsPublisher() {
    return new Publisher(
        new KatalMetricsDriverConsoleLogJson({
            log: console.table, // Using console.table to preserve data across page navigations
        }),
        console.error,
        new Context.Builder().withSite(getSiteName()).withServiceName(SERVICE_NAME).build()
    );
}

// We need to create our own publisher because it's not guaranteed the body page will be opted into katal metrics, otherwise
// we could use https://code.amazon.com/packages/KatalMetricsMons, which uses certain HTML meta tags to create the publisher.
function createDefaultMetricsPublisher() {
    try {
        const errorHandler = makeErrorHandler();
        const driverConfigBuilder = makeDriverConfigBuilder(errorHandler);
        const metricsContextBuilder = makeContextBuilder(errorHandler);
        return new Publisher(driverConfigBuilder.build(), errorHandler, metricsContextBuilder.build());
    } catch (error) {
        console.error('Could not initialize default metrics publisher. Falling back to console log.', error);
        return createConsoleLogMetricsPublisher();
    }
}

function makeErrorHandler() {
    return (err) => {
        if (!window.ueLogError) {
            console.error('VendorCentralTrimWebsite could not find window.ueLogError to handle error:', err.message);
            return;
        }

        window.ueLogError(err, ADDITIONAL_UE_LOG_ERROR_INFO);
    };
}

function makeDriverConfigBuilder(errorHandler) {
    const domainRealm = detectDomainRealm();
    if (errorHandler) {
        if (!domainRealm.domain) {
            errorHandler(new Error('VendorCentralTrimWebsite could not auto-detect domain for metrics'));
        }
        if (!domainRealm.realm) {
            errorHandler(new Error('VendorCentralTrimWebsite could not auto-detect realm for metrics'));
        }
    }

    // If we couldn't autodetect domain or realm, we already logged an error above
    // Now just use defaults, because some metrics are better than no metrics.
    const domain = domainRealm.domain || 'prod';
    const realm = domainRealm.realm || 'USAmazon';
    const builder = new KatalMetricsDriverSushi.Builder().withDomainRealm(domain, realm);

    if (errorHandler) {
        builder.withErrorHandler(errorHandler);
    }
    return builder;
}

function makeContextBuilder(errorHandler) {
    const contextBuilder = new Context.Builder().withSite(getSiteName()).withServiceName(SERVICE_NAME);
    if (window.ue_id) {
        contextBuilder.withRelatedMetrics(new Metric.String('http_request_id', window.ue_id));
    } else {
        if (errorHandler) {
            errorHandler(new Error('VendorCentralTrimWebsite could not detect http_request_id'));
        }
    }

    return contextBuilder;
}

let metricsPublisher; // We want a single metric publisher to be shared across all Vue apps

export function createMetricsPublisher() {
    const header = document.getElementById('vc-header-container');
    const logToConsole = header && header.getAttribute('data-katal-metrics-log-to-console');
    return logToConsole ? createConsoleLogMetricsPublisher() : createDefaultMetricsPublisher();
}

export default function getMetricsPublisher() {
    if (!metricsPublisher) {
        metricsPublisher = createMetricsPublisher();
    }
    return metricsPublisher;
}
